import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Check, Loader2, FolderPlus, FolderOpen } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useApi } from '../hooks/useApi';
import { entityNoun, isCompanySource } from '../utils/entityLabels';
import './list-selection-modal.css';

interface UserList {
  list_id: string;
  name: string;
  description?: string;
  contact_count: number;
  tags?: string[];
  is_default?: boolean;
  created_at: string;
  updated_at: string;
}

interface AccountList {
  list_id: string;
  owner_id: string;
  name: string;
  description?: string;
  account_count: number;
  tags?: string[];
  is_default?: boolean;
  created_at: string;
  updated_at: string;
}

interface ListSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (listIds: string[], selectAll: boolean, shouldRefreshLists: boolean, limit?: number) => void;
  selectedCount: number;
  selectAll: boolean;
  totalCount: number;
}

const ListSelectionModal: React.FC<ListSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedCount,
  selectAll,
  totalCount
}) => {
  const { showToast, state } = useAppContext();
  const { getLists, getAccountLists, createList, createAccountList } = useApi();
  
  const [lists, setLists] = useState<UserList[]>([]);
  const [accountLists, setAccountLists] = useState<AccountList[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedListIds, setSelectedListIds] = useState<Set<string>>(new Set());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creatingList, setCreatingList] = useState(false);
  const [shouldRefreshLists, setShouldRefreshLists] = useState(false);
  const [limit, setLimit] = useState<number | undefined>(undefined);

  // Simple count display
  const displayCount = selectAll ? totalCount : selectedCount;

  // Load lists based on data source
  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      if (state.searchState.dataSource === 'company') {
        // Load account lists for company data
        const data = await getAccountLists(true) as { success: boolean; lists?: AccountList[] };
        if (data?.success) {
          setAccountLists(data.lists || []);
        } else {
          throw new Error('Failed to load account lists');
        }
      } else {
        // Load contact lists for people data
        const data = await getLists(true) as { success: boolean; lists?: UserList[] };
        if (data?.success) {
          setLists(data.lists || []);
        } else {
          throw new Error('Failed to load contact lists');
        }
      }
    } catch (error) {
      console.error('Failed to load lists:', error);
      if (state.searchState.dataSource === 'company') {
        setAccountLists([]);
        showToast({
          type: 'error',
          title: 'Error Loading Lists',
          message: 'Failed to load account lists'
        });
      } else {
        setLists([]);
        showToast({
          type: 'error',
          title: 'Error Loading Lists',
          message: 'Failed to load contact lists'
        });
      }
    } finally {
      setLoading(false);
    }
  }, [getLists, getAccountLists, showToast, state.searchState.dataSource]);

  // Load lists when modal opens
  useEffect(() => {
    if (isOpen) {

      loadLists();
      // Reset form state
      setSelectedListIds(new Set());
      setShowCreateForm(false);
      setNewListName('');
      setShouldRefreshLists(false);
      setLimit(undefined);
    }
  }, [isOpen, loadLists]);

  // Handle list selection
  const toggleListSelection = useCallback((listId: string) => {
    setSelectedListIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(listId)) {
        newSet.delete(listId);
      } else {
        newSet.add(listId);
      }
      return newSet;
    });
  }, []);

  // Create new list
  const handleCreateList = useCallback(async () => {
    if (!newListName.trim()) {
      showToast({
        type: 'warning',
        title: 'List Name Required',
        message: 'Please enter a name for your new list'
      });
      return;
    }

    setCreatingList(true);
    try {
      if (state.searchState.dataSource === 'company') {
        // Create account list for company data
        const data = await createAccountList(
          newListName.trim(),
          undefined
        ) as { success: boolean; list_id?: string; list?: any };
        
        if (data?.success && data.list_id) {
          // Create a temporary AccountList object from the response
          const newList: AccountList = {
            list_id: data.list_id,
            name: newListName.trim(),
            description: data.list?.description || '',
            account_count: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          
          setAccountLists(prev => [newList, ...prev]);
          setSelectedListIds(prev => new Set([...prev, data.list_id!]));
          setShouldRefreshLists(true);
          
          showToast({
            type: 'success',
            title: 'Account List Created',
            message: `Successfully created "${newListName.trim()}"`
          });
        } else {
          throw new Error('Failed to create account list');
        }
      } else {
        // Create contact list for people data
        const data = await createList(
          newListName.trim(),
          undefined,
          [],
          false
        ) as { success: boolean; list?: UserList };
        
        if (data?.success && data.list) {
          setLists(prev => [data.list!, ...prev]);
          setSelectedListIds(prev => new Set([...prev, data.list!.list_id]));
          setShouldRefreshLists(true);
          
          showToast({
            type: 'success',
            title: 'List Created',
            message: `Successfully created "${data.list.name}"`
          });
        } else {
          throw new Error('Failed to create contact list');
        }
      }
      
      setNewListName('');
      setShowCreateForm(false);
    } catch {
      showToast({
        type: 'error',
        title: 'Error Creating List',
        message: 'Failed to create list'
      });
    } finally {
      setCreatingList(false);
    }
  }, [newListName, createList, createAccountList, showToast, state.searchState.dataSource]);

  // Handle confirmation
  const handleConfirm = useCallback(() => {
    if (selectedListIds.size === 0) {
      showToast({
        type: 'warning',
        title: 'No Lists Selected',
        message: 'Please select at least one list or create a new one'
      });
      return;
    }
    
    
    // Only use selectAll if there are no individual selections
    const actualSelectAll = selectAll && selectedCount === 0;
    onConfirm(Array.from(selectedListIds), actualSelectAll, shouldRefreshLists, limit);
    onClose();
  }, [selectedListIds, selectAll, shouldRefreshLists, limit, onConfirm, onClose, showToast, selectedCount, totalCount, displayCount]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content list-selection-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{state.searchState.dataSource === 'people' ? 'Add Contacts' : state.searchState.dataSource === 'company' ? 'Unlock Companies' : isCompanySource(state.searchState.dataSource) ? 'Add Companies' : 'Add to List'}</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="selection-summary">
            {state.searchState.dataSource === 'people' ? (
              <span>Unlocking {displayCount.toLocaleString()} {entityNoun(state.searchState.dataSource, displayCount)}:</span>
            ) : state.searchState.dataSource === 'company' ? (
              <span>Unlocking {displayCount.toLocaleString()} {entityNoun(state.searchState.dataSource, displayCount)}:</span>
            ) : (
              <span>Adding {displayCount.toLocaleString()} {entityNoun(state.searchState.dataSource, displayCount)} to:</span>
            )}
          </div>

          {/* Limit Input Section - Only show when selectAll is true */}
          {selectAll && (
            <div className="limit-section">
              <div className="form-group">
                <label htmlFor="limit">Limit number of {entityNoun(state.searchState.dataSource)} (optional):</label>
                <input
                  type="number"
                  id="limit"
                  value={limit || ''}
                  onChange={(e) => setLimit(e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder={`Max ${totalCount.toLocaleString()} available`}
                  min="1"
                  max={totalCount}
                  className="limit-input"
                />
              </div>
            </div>
          )}

          {/* Create New List Section */}
          <div className="create-list-section">
            <button
              className={`create-list-toggle ${showCreateForm ? 'active' : ''}`}
              onClick={() => setShowCreateForm(!showCreateForm)}
              disabled={creatingList}
            >
              <FolderPlus size={16} />
              <span>Create New List</span>
            </button>

            {showCreateForm && (
              <div className="create-list-form">
                <div className="form-group">
                  <label htmlFor="new-list-name">List Name *</label>
                  <input
                    id="new-list-name"
                    type="text"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    placeholder="Enter list name..."
                    maxLength={200}
                    disabled={creatingList}
                  />
                </div>
                <div className="form-actions">
                  <button
                    className="button-primary"
                    onClick={handleCreateList}
                    disabled={creatingList || !newListName.trim()}
                  >
                    {creatingList ? (
                      <>
                        <Loader2 size={16} className="spinning" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Create List
                      </>
                    )}
                  </button>
                  <button
                    className="button-secondary"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewListName('');
                    }}
                    disabled={creatingList}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Existing Lists Section */}
          <div className="existing-lists-section">
            <h3>Existing {state.searchState.dataSource === 'company' ? 'Account Lists' : 'Lists'}</h3>
            
            {loading ? (
              <div className="loading-state">
                <Loader2 size={20} className="spinning" />
                <span>Loading lists...</span>
              </div>
            ) : (state.searchState.dataSource === 'company' ? accountLists.length === 0 : lists.length === 0) ? (
              <div className="empty-state">
                <FolderOpen size={48} />
                <p>No {state.searchState.dataSource === 'company' ? 'account lists' : 'lists'} found</p>
                <p>Create your first {state.searchState.dataSource === 'company' ? 'account list' : 'list'} to get started</p>
              </div>
            ) : (
              <div className="lists-grid">
                {(state.searchState.dataSource === 'company' ? accountLists : lists).map(list => (
                    <div
                      key={list.list_id}
                      className={`list-item ${selectedListIds.has(list.list_id) ? 'selected' : ''}`}
                      onClick={() => toggleListSelection(list.list_id)}
                    >
                      <div className="list-item-header">
                        <div className="list-item-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedListIds.has(list.list_id)}
                            onChange={() => toggleListSelection(list.list_id)}
                          />
                          <div className="checkbox-visual">
                            {selectedListIds.has(list.list_id) && <Check size={10} />}
                          </div>
                        </div>
                        <div className="list-item-info">
                          <h4 className="list-name">
                            {list.name}
                            {list.is_default && <span className="default-badge">Default</span>}
                          </h4>
                          <div className="list-meta">
                            <span className="contact-count">
                              {state.searchState.dataSource === 'company' 
                                ? `${((list as AccountList).account_count ?? 0).toLocaleString()} account${((list as AccountList).account_count ?? 0) !== 1 ? 's' : ''}`
                                : `${((list as UserList).contact_count ?? 0).toLocaleString()} contact${((list as UserList).contact_count ?? 0) !== 1 ? 's' : ''}`
                              }
                            </span>
                            {list.tags && list.tags.length > 0 && (
                              <div className="list-tags">
                                {list.tags.slice(0, 3).map(tag => (
                                  <span key={tag} className="tag">{tag}</span>
                                ))}
                                {list.tags.length > 3 && (
                                  <span className="tag-more">+{list.tags.length - 3}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <div className="selection-info">
            {selectedListIds.size > 0 && (
              <span>{selectedListIds.size} list{selectedListIds.size !== 1 ? 's' : ''} selected</span>
            )}
          </div>
          <div className="modal-actions">
            <button className="button-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button-primary"
              onClick={handleConfirm}
              disabled={selectedListIds.size === 0}
            >
              {state.searchState.dataSource === 'people' 
                ? `Unlock${selectedListIds.size > 1 ? ` & Add to ${selectedListIds.size} Lists` : ' Contacts'}`
                : state.searchState.dataSource === 'company'
                ? `Unlock${selectedListIds.size > 1 ? ` & Add to ${selectedListIds.size} Lists` : ' Companies'}`
                : `Add to List${selectedListIds.size !== 1 ? 's' : ''}`
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListSelectionModal;